import { useContext, useRef } from 'react';
import {
    ActionDependant,
    evaluateTemplateValue,
    GlobalDataContext,
    propsDataLocationToPathAndValue,
    TemplateContext,
} from '@ea-lab/reactive-json';
import { getTagColorClass, getTagRemoveBtnClass } from '../../utils/tagColors';

/**
 * TaskTagsField — reactive-json form element for multi-tag selection.
 *
 * Displays current tags as colored pills with a remove button, plus a "+"
 * text input with datalist autocomplete. Selecting from the datalist adds
 * the tag immediately; Enter and blur also work.
 *
 * Color scheme (matching KanbanCardPlugins):
 *   Urgent → red | Dev/Backend/Bug/Ops → purple | others → gray
 *
 * Props:
 *   dataLocation   – path to the stored TID array (e.g. ~~.taskSyncable.data.tags)
 *   dynamicOptions – array of { value: TID, label: string } from the taxonomy
 */
export const TaskTagsField = ({ props, datafield, path }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);
    const inputRef = useRef(null);

    const { formData: currentTids, formDataPath } = propsDataLocationToPathAndValue({
        currentPath: path,
        datafield,
        dataLocation: props.dataLocation,
        defaultValue: [],
        globalDataContext,
        templateContext,
    });

    const options = evaluateTemplateValue({
        valueToEvaluate: props.dynamicOptions,
        globalDataContext,
        templateContext,
    }) || [];

    const tags = Array.isArray(currentTids) ? currentTids : [];

    const findTid = (label) =>
        options.find(o => o.label.toLowerCase() === label.toLowerCase())?.value ?? null;

    const findLabel = (tid) =>
        options.find(o => String(o.value) === String(tid))?.label ?? String(tid);

    const tryAdd = (rawValue) => {
        const val = rawValue.trim();
        if (!val) return false;
        const tid = findTid(val);
        if (tid && !tags.map(String).includes(String(tid))) {
            globalDataContext.updateData([...tags, String(tid)], formDataPath);
            if (inputRef.current) inputRef.current.value = '';
            return true;
        }
        return false;
    };

    const removeTag = (tid) => {
        globalDataContext.updateData(tags.filter(t => String(t) !== String(tid)), formDataPath);
    };

    // Fires on every keystroke — catches datalist selection immediately
    // (when user clicks a suggestion, onChange fires with the full label).
    const handleChange = (e) => {
        tryAdd(e.target.value);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            tryAdd(e.target.value);
        }
    };

    // Fallback for partial matches on blur
    const handleBlur = (e) => tryAdd(e.target.value);

    const datalistId = `task-tags-${formDataPath.replace(/\./g, '-')}`;

    return (
        <ActionDependant {...props}>
            <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
                {tags.map((tid) => {
                    const label = findLabel(tid);
                    return (
                        <span
                            key={tid}
                            className={`px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 border shadow-sm ${getTagColorClass(label)}`}
                        >
                            {label}
                            <button
                                type="button"
                                className={`focus:outline-none ${getTagRemoveBtnClass(label)}`}
                                onClick={() => removeTag(tid)}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </span>
                    );
                })}

                <div className="relative inline-block">
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder="+ Tag"
                        list={datalistId}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        onBlur={handleBlur}
                        className="px-2.5 py-1 border border-dashed border-slate-300 text-slate-500 rounded-full text-xs hover:bg-slate-100 hover:text-slate-700 hover:border-slate-400 transition-all font-medium focus:ring-2 focus:ring-iris-500 focus:border-transparent focus:bg-white w-24 focus:w-32 placeholder-slate-400"
                    />
                    <datalist id={datalistId}>
                        {options
                            .filter(o => !tags.map(String).includes(String(o.value)))
                            .map(o => (
                                <option key={o.value} value={o.label} />
                            ))}
                    </datalist>
                </div>
            </div>
        </ActionDependant>
    );
};
